import { useState, useEffect, useCallback } from 'react';
import { useGameSocket } from '../hooks/useGameSocket';
import './GameInterface.css';

interface Question {
  id: string;
  sequence: number;
  total: number;
  text: string;
  textEnglish?: string;
  imageUrl?: string;
  options: { id: string; text: string; textEnglish?: string }[];
  expiresAt: string;
}

interface Props {
  username: string;
}

interface Sponsor {
  name: string;
  imageUrl: string;
  link: string;
}

export default function GameInterface({ username }: Props) {
  const getWsUrl = () => {
    if (process.env.REACT_APP_WS_URL) return process.env.REACT_APP_WS_URL;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    return `${protocol}//${host}/ws`;
  };
  const wsUrl = getWsUrl();
  const url = `${wsUrl}?username=${encodeURIComponent(username)}`;
  const handleSocketMessage = useCallback((msg: any) => {
    if (msg.type === 'SESSION_ID' || msg.type.toUpperCase() === 'SESSION_ID') {
      const sid = String(msg.data || '');
      setSessionId(sid);
      console.log("Session ID initialized via callback:", sid);
      setAppState(prev => (prev === 'connecting' ? 'waiting' : prev));
    }
  }, []);

  const { isConnected, latestMessage, sendMessage } = useGameSocket(url, handleSocketMessage);

  const [question, setQuestion] = useState<Question | null>(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [correctOptionId, setCorrectOptionId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [appState, setAppState] = useState<'connecting' | 'waiting' | 'playing' | 'finished'>('connecting');
  const [language, setLanguage] = useState<'hindi' | 'english'>('hindi');
  const [gameOverInfo, setGameOverInfo] = useState<{ winner?: string; score?: number; rankings?: any[] } | null>(null);
  const [sponsor, setSponsor] = useState<Sponsor | null>(null);
  const [nextQuiz, setNextQuiz] = useState<{ title: string; time: string } | null>(null);
  const [playerCount, setPlayerCount] = useState<number>(0);

  const getQuestionText = () => {
    if (!question) return 'Question loading...';
    if (language === 'english' && question.textEnglish) return question.textEnglish;
    return question.text;
  };

  const getOptionText = (opt: { id: string; text: string; textEnglish?: string }) => {
    if (language === 'english' && opt.textEnglish) return opt.textEnglish;
    return opt.text;
  };

  const englishAvailable =
    !!question?.textEnglish && question.options?.every((o) => typeof o.textEnglish === 'string' && o.textEnglish.length > 0);

  // Debug state changes
  useEffect(() => {
    console.log("State update:", {
      connected: isConnected,
      sessionId: !!sessionId,
      question: !!question,
      appState,
      timeLeft,
      hasAnswered,
    });
  }, [isConnected, sessionId, question, appState, timeLeft, hasAnswered]);

  useEffect(() => {
    if (!latestMessage) return;

    // SESSION_ID is now handled in callback, but keep for fallback or if needed
    if (latestMessage.type === 'SESSION_ID' || latestMessage.type.toUpperCase() === 'SESSION_ID') {
      const sid = String(latestMessage.data || '');
      if (!sessionId) setSessionId(sid);
      if (question) {
        setAppState('playing');
      } else {
        setAppState('waiting');
      }
      return;
    }

    if (latestMessage.type === 'NEW_QUESTION') {
      const q = latestMessage.data;
      console.log("NEW_QUESTION received:", q);
      if (q && q.id && q.text && q.options?.length > 0) {
        console.log("Valid question set:", q.text);
        setQuestion(q);
        setHasAnswered(false);
        setFeedback(null);
        setSelectedOptionId(null);
        setCorrectOptionId(null);
        setGameOverInfo(null);

        // Reset score only if it's explicitly required by server or game start
        // Removed setScore(0) on sequence === 1 to let server manage initial state.

        const endTime = new Date(q.expiresAt).getTime();
        const initialRemaining = Math.max(0, Math.floor((endTime - Date.now()) / 1000));
        setTotalTime(initialRemaining || 30);
        const interval = setInterval(() => {
          const remaining = Math.max(0, Math.floor((endTime - Date.now()) / 1000));
          setTimeLeft(remaining);
          if (remaining <= 0) clearInterval(interval);
        }, 1000);

        // Move to playing state when question is loaded
        setAppState('playing');

        return () => clearInterval(interval);
      } else {
        console.log("Invalid/empty question data:", q);
      }
    }

    if (latestMessage.type === 'GAME_OVER') {
      const data = latestMessage.data || {};
      setQuestion(null);
      setHasAnswered(false);
      setTimeLeft(0);
      setAppState('finished');
      setGameOverInfo({
        winner: data.winner || data.topPlayer || '',
        score:
          typeof data.score === 'number'
            ? data.score
            : typeof data.topScore === 'number'
              ? data.topScore
              : undefined,
        rankings: data.rankings || [],
      });
      return;
    }

    if (latestMessage.type === 'SCORE_UPDATE') {
      const newScore = latestMessage.data?.totalScore ?? latestMessage.data?.score;
      if (typeof newScore === 'number') {
        setScore(newScore);
        console.log("Score updated to:", newScore);
      }
    }

    if (latestMessage.type === 'ANSWER_FEEDBACK') {
      setFeedback(latestMessage.data.isCorrect ? 'correct' : 'wrong');
      const feedbackScore = latestMessage.data.score;
      if (typeof feedbackScore === 'number') {
        setScore(feedbackScore);
      }
      const correctId = latestMessage.data.correctId || latestMessage.data.correctID;
      if (correctId) {
        setCorrectOptionId(correctId);
      }
      console.log("Answer feedback:", latestMessage.data.isCorrect ? 'Correct' : 'Wrong', "New score:", feedbackScore);
    }

    if (latestMessage.type === 'SPONSOR_UPDATE') {
      setSponsor(latestMessage.data);
      console.log("Sponsor updated:", latestMessage.data);
    }

    if (latestMessage.type === 'NEXT_QUIZ_UPDATE') {
      setNextQuiz(latestMessage.data);
      console.log("Next quiz updated:", latestMessage.data);
    }

    if (latestMessage.type === 'SHOW_SPONSOR') {
      setAppState('waiting');
      if (latestMessage.data) {
        setSponsor(latestMessage.data);
      }
      console.log("Showing post-game sponsor:", latestMessage.data);
    }

    if (latestMessage.type === 'PLAYER_COUNT_UPDATE') {
      setPlayerCount(latestMessage.data);
      console.log("Player count updated:", latestMessage.data);
    }
  }, [latestMessage]);

  const handleAnswer = (optionId: string) => {
    console.log("Button clicked! optionId:", optionId);
    // TEMPORARY DEBUG ALERT
    // alert("Tapped option: " + optionId);

    if (!isConnected) {
      console.log("Cannot answer: socket not ready");
      alert("Connection lost. Please wait for reconnection.");
      return;
    }
    if (!sessionId) {
      console.log("Cannot answer: sessionId not received yet");
      alert("Error: Game session not initialized. Please refresh.");
      return;
    }
    if (hasAnswered) {
      console.log("Cannot answer: already answered");
      return;
    }
    if (timeLeft <= 0) {
      console.log("Cannot answer: time expired");
      return;
    }
    if (!question) {
      console.log("Cannot answer: no question loaded");
      return;
    }

    console.log("Sending answer now...");

    setHasAnswered(true);
    setSelectedOptionId(optionId);

    const success = sendMessage({
      type: 'SUBMIT_ANSWER',
      sessionId: sessionId,
      questionId: question.id,
      answer: optionId,
    });

    if (success) {
      console.log("Answer sent successfully");
    } else {
      console.error("Failed to send answer");
      setHasAnswered(false);
      alert("Failed to send answer. Please try again.");
    }
  };

  const handleShare = () => {
    const shareData = {
      title: 'Crack It Quizzes',
      text: 'Join me for a live quiz on Crack It!',
      url: window.location.origin,
    };

    if (navigator.share) {
      navigator.share(shareData)
        .then(() => console.log('Successful share'))
        .catch((error) => console.log('Error sharing', error));
    } else {
      navigator.clipboard.writeText(shareData.url);
      alert('Website URL copied to clipboard!');
    }
  };

  return (
    <div className="game-container">
      <div className="max-width-wrapper">
        {/* Compact Header */}
        <div className="compact-header">
          <div className="header-main">
            <img src="/logo_custom.png" alt="Logo" className="header-logo-small" />
            <h1 className="main-title-small">Crack It Quizzes</h1>
          </div>

          <div className="header-info-bar">
            <div className="info-item">
              👤 <span className="info-label">User:</span> <strong className="info-value-user">{username}</strong>
            </div>
            <div className="info-item">
              👥 <span className="info-label">Online:</span> <strong className="info-value-count">{playerCount}</strong>
            </div>
            <button className="share-button-mini" onClick={handleShare}>
              📤 Share
            </button>
          </div>
        </div>

        {/* Timer & Score */}
        <div className="stats-card">
          <div className="stats-row">
            <div className="stat-text">
              Time left:{' '}
              <span
                style={{
                  fontWeight: 'bold',
                  color: timeLeft <= 10 ? '#e74c3c' : timeLeft <= 20 ? '#f39c12' : '#2ecc71',
                }}
              >
                {timeLeft}s
              </span>
            </div>

            <div className="stat-text">
              Score:{' '}
              <span
                style={{
                  fontWeight: 'bold',
                  color: score >= 0 ? '#2ecc71' : '#e74c3c',
                }}
              >
                {score}
              </span>
            </div>
          </div>

          {totalTime > 0 && (
            <div>
              <div
                style={{
                  height: '12px',
                  borderRadius: '999px',
                  background: 'rgba(0,0,0,0.25)',
                  overflow: 'hidden',
                  boxShadow: 'inset 0 0 4px rgba(0,0,0,0.6)',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${Math.max(0, Math.min(100, (timeLeft / totalTime) * 100))}%`,
                    background:
                      timeLeft <= 10 ? '#e74c3c' : timeLeft <= 20 ? '#f1c40f' : '#2ecc71',
                    transition: 'width 0.4s linear',
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        {appState === 'connecting' || !isConnected ? (
          <div style={{ textAlign: 'center', margin: '100px 0' }}>
            <div
              style={{
                border: '8px solid rgba(255,255,255,0.2)',
                borderTop: '8px solid #3498db',
                borderRadius: '50%',
                width: '80px',
                height: '80px',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 30px',
              }}
            />
            <p style={{ fontSize: '1.6rem' }}>
              {!isConnected ? 'Reconnecting to game server...' : 'Connecting to game server...'}
            </p>
          </div>
        ) : appState === 'waiting' ? (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 20px',
              background: 'rgba(255,255,255,0.08)',
              borderRadius: '20px',
              color: '#bdc3c7',
            }}
          >
            <div style={{ fontSize: '2rem', marginBottom: '30px' }}>
              Waiting for the host to start the next question...
            </div>

            {nextQuiz && nextQuiz.title && (
              <div
                style={{
                  marginBottom: '30px',
                  padding: '25px',
                  background: 'rgba(52, 152, 219, 0.15)',
                  borderRadius: '16px',
                  border: '2px solid rgba(52, 152, 219, 0.3)',
                  animation: 'fadeIn 1s ease-in',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                }}
              >
                <div style={{ fontSize: '1.1rem', marginBottom: '12px', color: '#3498db', fontWeight: 'bold', letterSpacing: '2px', textTransform: 'uppercase' }}>
                  Next Quiz Announcement
                </div>
                <div style={{ fontSize: '2.2rem', fontWeight: 800, color: '#ffffff', marginBottom: '8px' }}>
                  {nextQuiz.title}
                </div>
                <div style={{ fontSize: '1.5rem', color: '#f1c40f', fontWeight: 600 }}>
                  🕒 {nextQuiz.time}
                </div>
              </div>
            )}

            {sponsor && (
              <div
                style={{
                  marginTop: '40px',
                  padding: '30px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '16px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  animation: 'fadeIn 1s ease-in',
                }}
              >
                <div style={{ fontSize: '1.2rem', marginBottom: '20px', color: '#f1c40f', fontWeight: 'bold', letterSpacing: '2px' }}>
                  TODAY'S SPONSOR
                </div>
                <a
                  href={sponsor.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                >
                  <img
                    src={sponsor.imageUrl}
                    alt={sponsor.name}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '200px',
                      borderRadius: '8px',
                      marginBottom: '15px',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.3)'
                    }}
                  />
                  <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#ffffff' }}>
                    {sponsor.name}
                  </div>
                </a>
              </div>
            )}
          </div>
        ) : appState === 'finished' ? (
          <div
            style={{
              textAlign: 'center',
              padding: '80px 20px',
              background: 'rgba(255,255,255,0.08)',
              borderRadius: '20px',
              fontSize: '2rem',
              color: '#bdc3c7',
            }}
          >
            <h2
              style={{
                fontSize: '3rem',
                marginBottom: '20px',
                color: '#ecf0f1',
              }}
            >
              Game Over
            </h2>
            {gameOverInfo?.winner ? (
              <p style={{ fontSize: '2rem', marginBottom: '10px' }}>
                Winner:{' '}
                <strong style={{ color: '#2ecc71' }}>{String(gameOverInfo.winner)}</strong>
              </p>
            ) : (
              <p style={{ fontSize: '1.8rem', marginBottom: '10px' }}>
                Thanks for playing!
              </p>
            )}
            {typeof gameOverInfo?.score === 'number' && (
              <p style={{ fontSize: '1.6rem', color: '#f1c40f', marginBottom: '30px' }}>
                Winning score: <strong>{Math.floor(gameOverInfo.score)}</strong>
              </p>
            )}

            {gameOverInfo?.rankings && gameOverInfo.rankings.length > 0 && (
              <div className="leaderboard-container-mini">
                <h3 className="leaderboard-title-mini">🏆 Top 10 Elite Players</h3>
                <div className="leaderboard-table-wrapper">
                  <table className="leaderboard-table-mini">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Player</th>
                        <th>Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gameOverInfo.rankings.map((r, index) => (
                        <tr key={index} className={r.Member === username ? 'current-player-row' : ''}>
                          <td>{index + 1}</td>
                          <td>{r.Member}</td>
                          <td>{Math.floor(r.Score)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <p style={{ marginTop: '30px', fontSize: '1.4rem', color: '#bdc3c7' }}>
              You can wait here for the host to start a new game.
            </p>
          </div>
        ) : (
          <div className="question-card">
            {/* Question Counter Badge */}
            <div style={{
              position: 'absolute',
              top: '-15px',
              left: '20px',
              fontSize: '1rem',
              background: '#3498db',
              padding: '6px 12px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              fontWeight: 800,
              zIndex: 20,
              boxShadow: '0 4px 12px rgba(52, 152, 219, 0.4)',
              border: '1px solid rgba(255,255,255,0.1)'
            }}>
              {question ? `${question.sequence}/${question.total}` : '?'}
            </div>

            <div className="question-header">
              <h2 className="question-title">{getQuestionText()}</h2>
              <div className="language-toggle-wrapper" style={{ textAlign: 'right', minWidth: '170px' }}>
                <button
                  type="button"
                  onClick={() => setLanguage((prev) => (prev === 'hindi' ? 'english' : 'hindi'))}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '999px',
                    border: '1px solid rgba(52, 152, 219, 0.9)',
                    background: 'rgba(0,0,0,0.25)',
                    color: '#ecf0f1',
                    cursor: 'pointer',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  {language === 'hindi' ? 'Show English' : 'हिंदी दिखाएँ'}
                </button>
                <div style={{ marginTop: '6px', fontSize: '1.05rem', color: '#bdc3c7' }}>
                  Language:{' '}
                  <strong style={{ color: '#ecf0f1' }}>
                    {language === 'english' ? 'English' : 'Hindi'}
                  </strong>
                  {language === 'english' && !englishAvailable ? (
                    <span style={{ display: 'block', color: '#f39c12' }}>
                      Not available
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Question Image (optional) */}
            {question?.imageUrl && (
              <div style={{ textAlign: 'center', margin: '16px 0' }}>
                <img
                  src={question.imageUrl}
                  alt="Question visual"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '260px',
                    borderRadius: '12px',
                    objectFit: 'contain',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                    border: '1px solid rgba(255,255,255,0.12)',
                  }}
                />
              </div>
            )}

            <div className="options-grid">
              {question?.options.map((opt: any) => {
                let extraClass = '';
                if (hasAnswered && correctOptionId) {
                  if (opt.id === correctOptionId) {
                    extraClass = ' correct-option';
                  } else if (opt.id === selectedOptionId && feedback === 'wrong') {
                    extraClass = ' wrong-option';
                  }
                }

                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => handleAnswer(opt.id)}
                    onTouchStart={() => { }}
                    disabled={hasAnswered || timeLeft <= 0}
                    className={`option-button${extraClass}`}
                  >
                    {opt.id.toUpperCase()}. {getOptionText(opt)}
                  </button>
                );
              })}
            </div>

            {hasAnswered && (
              <div
                style={{
                  marginTop: '35px',
                  padding: '20px',
                  background: 'rgba(46,204,113,0.15)',
                  borderRadius: '12px',
                  textAlign: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: '#2ecc71',
                }}
              >
                Answer submitted! Waiting for result...
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
